import React, { useCallback, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import useWidgetFilterNavigator from "../../../../useWidgetFilterNavigator";
import TopFeatureTable, {
  BaseTopFeatureRow,
  WidgetTableProps,
} from "../TopFeatureTable";
import { GET_TOP_PACKAGES_DATA } from "../../../../WidgetDataApi";
import useQueryWithRetries from "../../../../../Tools/useQueryWithRetries";
import {
  Tooltip,
  TableCell,
  tableCellTypesEnum,
  formatNumber,
} from "@SeemplicityDev/shared";

interface TopPackageRow extends BaseTopFeatureRow {
  package: string;
  resources: number;
}

const TopPackagesTable: React.FC<WidgetTableProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  id,
  isForExport = false,
  setTotalWidgets = null,
  index = null,
  setIsReadyToExport = null,
}) => {
  const { data, error, loading, refetch } = useQueryWithRetries({
    query: GET_TOP_PACKAGES_DATA,

    variables: {
      widget_definition_key: "top_10_vulnerable_packages",
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id ?? null,
  });

  const onChartClick = useCallback(
    (severity: string, { package: package_name }: TopPackageRow) => {
      onFilterClick([
        ["in", "severity", severity],
        ["in", "package_name", package_name],
      ]);
    },
    [onFilterClick]
  );

  const columnHelper = createColumnHelper<TopPackageRow>();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("package", {
        cell: (props) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <Tooltip content={props.getValue()}>
              <span className="package-cell">{props.getValue()}</span>
            </Tooltip>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Package",
        minSize: 220,
      }),
      columnHelper.accessor("resources", {
        cell: (props) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <span className="resources-cell">
              {formatNumber(props.getValue())}
            </span>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Resources",
      }),
    ];
  }, []);

  return (
    <TopFeatureTable
      columns={columns}
      data={data}
      error={error}
      loading={loading}
      refetch={refetch}
      onChartClick={onChartClick}
      id={id}
      isForExport={isForExport}
      setTotalWidgets={setTotalWidgets}
      index={index}
      identifier={"package"}
      setIsReadyToExport={setIsReadyToExport}
    />
  );
};

export default TopPackagesTable;
